import { useEffect, useMemo, useState } from "react";
import {
  ConfirmDialog,
  ExtendedCard,
  FormAlert,
  TableKit,
} from "@NP-Webber/crm-kit";
import {
  formIntakeApi,
  type FormIntakeDeliveryDto,
  type FormIntakeDto,
} from "../../api/formIntakeApi";
import { toClientApiError } from "../../api/httpClient";
import {
  DeleteOutlinedIcon,
  EditOutlinedIcon,
} from "../../ui/appIcons";
import { QuietButton } from "../../ui/QuietButton";
import { FormIntakeWizardDialog } from "./FormIntakeWizardDialog";

const STATUS_LABELS: Record<string, string> = {
  created: "נוצר מנוי",
  duplicate: "כפילות",
  unmatched: "קופה לא נמצאה",
  ambiguous: "מספר קופות",
  validation_error: "שגיאת ולידציה",
  accepted: "התקבל",
};

export type FormIntakeCardProps = {
  open: boolean;
  intake: FormIntakeDto | null;
  onClose: () => void;
  onChanged?: () => void;
};

export function FormIntakeCard({
  open,
  intake,
  onClose,
  onChanged,
}: FormIntakeCardProps) {
  const [active, setActive] = useState<FormIntakeDto | null>(intake);
  const [deliveries, setDeliveries] = useState<FormIntakeDeliveryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reloadDeliveries = (id: string) => {
    setLoading(true);
    void formIntakeApi
      .listDeliveries(id)
      .then(setDeliveries)
      .catch(() => setDeliveries([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !intake) return;
    setActive(intake);
    setError(null);
    setEditOpen(false);
    setDeleteOpen(false);
    reloadDeliveries(intake.id);
  }, [open, intake]);

  const fieldMapSummary = useMemo(() => {
    const map = active?.fieldMap ?? {};
    return Object.entries(map)
      .filter(([, v]) => v)
      .map(([k, v]) => `${k} ← ${v}`)
      .join(" · ");
  }, [active]);

  const curlExample = useMemo(() => {
    if (!active) return "";
    const body = JSON.stringify(active.samplePayload ?? {}, null, 2);
    return `curl -X POST "${active.webhookUrl}" \\\n  -H "Content-Type: application/json" \\\n  -d '${body.replace(/'/g, "'\\''")}'`;
  }, [active]);

  const detailsFields = useMemo(
    () => [
      { key: "name", label: "שם", value: active?.name ?? "" },
      {
        key: "externalFormId",
        label: "מזהה טופס חיצוני",
        value: active?.externalFormId ?? "",
      },
      {
        key: "isActive",
        label: "פעיל",
        value: active?.isActive ? "כן" : "לא",
      },
      {
        key: "fieldMap",
        label: "מיפוי",
        value: fieldMapSummary || "—",
      },
      {
        key: "webhookUrl",
        label: "כתובת Webhook",
        value: active?.webhookUrl ?? "—",
      },
    ],
    [active, fieldMapSummary],
  );

  const handleDelete = async () => {
    if (!active) return;
    setBusy(true);
    setError(null);
    try {
      await formIntakeApi.remove(active.id);
      setDeleteOpen(false);
      onChanged?.();
      onClose();
    } catch (err) {
      setError(toClientApiError(err).message);
      setDeleteOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const handleRotate = async () => {
    if (!active) return;
    setBusy(true);
    setError(null);
    try {
      const next = await formIntakeApi.rotateToken(active.id);
      setActive(next);
      onChanged?.();
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  if (!open || !active) return null;

  const deliveryRows = deliveries.map((d) => ({
    id: d.id,
    createdAt: new Date(d.createdAt).toLocaleString("he-IL"),
    statusLabel: STATUS_LABELS[d.status] ?? d.status,
    externalSubmissionId: d.externalSubmissionId ?? "—",
    errorMessage: d.errorMessage ?? "—",
  }));

  const deliveriesTab = (
    <div className="page-section__table-surface">
      <TableKit
        columns={[
          {
            key: "createdAt",
            title: "תאריך",
            sortable: true,
            filterable: true,
            minWidth: 140,
          },
          {
            key: "statusLabel",
            title: "סטטוס",
            sortable: true,
            filterable: true,
            minWidth: 120,
          },
          {
            key: "externalSubmissionId",
            title: "מזהה שליחה",
            filterable: true,
            minWidth: 120,
          },
          {
            key: "errorMessage",
            title: "הודעה",
            filterable: true,
            minWidth: 180,
          },
        ]}
        data={deliveryRows as unknown as Array<Record<string, unknown>>}
        loading={loading}
        clientSideMode
        emptyMessage="אין קאלבקים עדיין"
        showFilters
        showColumnPicker
      />
    </div>
  );

  const guideTab = (
    <div style={{ padding: "12px 4px", display: "grid", gap: 12 }}>
      <FormAlert severity="info">
        ממערכת הטפסים החיצונית שלחו POST לכתובת ה-Webhook עם Content-Type:
        application/json. זיהוי קופה לפי טלפון/אימייל ממופים; הקטגוריה קבועה
        בהגדרה.
      </FormAlert>
      <label className="file-import-picker">
        <span className="file-import-picker__label">דוגמת curl</span>
        <textarea
          readOnly
          rows={10}
          value={curlExample}
          dir="ltr"
          style={{
            width: "100%",
            fontFamily: "ui-monospace, monospace",
            fontSize: 12,
            padding: 8,
            boxSizing: "border-box",
          }}
        />
      </label>
      <QuietButton onClick={() => void handleRotate()} disabled={busy}>
        חידוש טוקן (מייצר URL חדש)
      </QuietButton>
    </div>
  );

  return (
    <>
      <ExtendedCard
        key={active.id}
        open
        onClose={onClose}
        title={active.name}
        subtitle={active.isActive ? "קליטה פעילה" : "קליטה מושבתת"}
        detailsFields={detailsFields}
        headerActions={[
          {
            icon: <EditOutlinedIcon />,
            tooltip: "עריכה",
            onClick: () => {
              setError(null);
              setEditOpen(true);
            },
          },
          {
            icon: <DeleteOutlinedIcon />,
            tooltip: "מחיקה",
            onClick: () => setDeleteOpen(true),
          },
        ]}
        detailsHeaderExtra={
          error && !editOpen ? <FormAlert severity="error">{error}</FormAlert> : null
        }
        feedSources={{ activities: [], tasks: [], notes: [] }}
        hideFeedTab
        showDashboard={false}
        defaultTab={0}
        extraTabs={[
          { label: "קאלבקים", content: deliveriesTab },
          { label: "הנחיית שליחה", content: guideTab },
        ]}
      />

      <FormIntakeWizardDialog
        open={editOpen}
        initial={active}
        onClose={() => {
          if (!busy) setEditOpen(false);
        }}
        onSaved={(item) => {
          setActive(item);
          onChanged?.();
          reloadDeliveries(item.id);
        }}
      />

      <ConfirmDialog
        open={deleteOpen}
        onClose={() => {
          if (!busy) setDeleteOpen(false);
        }}
        title="מחיקת קליטת טפסים"
        message={`למחוק את הקליטה "${active.name}"?`}
        confirmText="מחיקה"
        cancelText="ביטול"
        loading={busy}
        onConfirm={() => {
          void handleDelete();
        }}
        onCancel={() => setDeleteOpen(false)}
      />
    </>
  );
}
